import { createFileRoute, notFound, redirect } from "@tanstack/react-router";
import { regions } from "@/data/regions";

/**
 * Oude WordPress-adressen (en veelvoorkomende varianten daarvan) permanent
 * doorsturen naar de bijbehorende pagina op de nieuwe site. Zo verdwijnt de
 * oude site uit Google en komt een bezoeker met een oude link altijd goed uit.
 */
const LEGACY_REDIRECTS: Record<string, string> = {
  "senioren-verhuizingen": "/diensten/seniorenverhuizing",
  "senioren-verhuizing": "/diensten/seniorenverhuizing",
  "seniorenverhuizingen": "/diensten/seniorenverhuizing",
  "spoedverhuizing": "/diensten/particuliere-verhuizing",
  "bedrijfsverhuizing": "/diensten/zakelijke-verhuizing",
  "bedrijfsverhuizingen": "/diensten/zakelijke-verhuizing",
  "verhuizingen": "/diensten/particuliere-verhuizing",
  "verhuizen-en-opslag": "/diensten/opslag",
  "inboedelopslag": "/diensten/opslag",
  "home": "/",

  "index": "/",
  "index-php": "/",
  "home-2": "/",
  "welkom": "/",
  "diensten-2": "/diensten",
  "onze-diensten": "/diensten",
  "services": "/diensten",
  "verhuizen": "/diensten/particuliere-verhuizing",
  "particulier": "/diensten/particuliere-verhuizing",
  "particulier-verhuizen": "/diensten/particuliere-verhuizing",
  "particuliere-verhuizing-2": "/diensten/particuliere-verhuizing",
  "zakelijk": "/diensten/zakelijke-verhuizing",
  "zakelijk-verhuizen": "/diensten/zakelijke-verhuizing",
  "kantoorverhuizing": "/diensten/zakelijke-verhuizing",
  "seniorenverhuizing": "/diensten/seniorenverhuizing",
  "verhuislift": "/diensten/verhuislift",
  "opslag": "/diensten/opslag",
  "opslagruimte": "/diensten/opslag",
  "ontruiming": "/diensten/ontruiming",
  "ontruimingen": "/diensten/ontruiming",
  "transport": "/diensten/transport",
  "evenementen": "/diensten/evenementenlogistiek",
  "evenementenlogistiek": "/diensten/evenementenlogistiek",
  "inpakservice": "/diensten/inpakservice",
  "over-ons-2": "/over-ons",
  "about": "/over-ons",
  "wie-zijn-wij": "/over-ons",
  "contact-2": "/contact",
  "contact-us": "/contact",
  "neem-contact-op": "/contact",
  "offerte-aanvragen": "/offerte",
  "gratis-offerte": "/offerte",
  "quote": "/offerte",
  "prijzen": "/offerte",
  "tarieven": "/offerte",
  "reviews-2": "/reviews",
  "beoordelingen": "/reviews",
  "referenties": "/reviews",
  "blog": "/verhuistips",
  "nieuws": "/verhuistips",
  "tips": "/verhuistips",
  "verhuistips-2": "/verhuistips",
  "werkgebieden": "/werkgebied",
  "regio": "/werkgebied",
  "regios": "/werkgebied",
  "privacy": "/privacybeleid",
  "privacy-policy": "/privacybeleid",
  "voorwaarden": "/algemene-voorwaarden",
  "algemene-voorwaarden-2": "/algemene-voorwaarden",
};

/** Paden van de oude WordPress-installatie die nergens meer op de site bestaan. */
const WORDPRESS_PREFIXES = [
  "wp-admin",
  "wp-login.php",
  "wp-content",
  "wp-includes",
  "wp-json",
  "xmlrpc.php",
  "wp-cron.php",
  "wp-signup.php",
  "author",
  "category",
  "tag",
  "feed",
  "comments",
  "wp-sitemap.xml",
];

function resolveLegacyTarget(rawPath: string): string | null {
  const clean = rawPath.replace(/^\/+/, "").replace(/\/+$/, "").toLowerCase();
  if (!clean) return "/";

  const segments = clean.split("/");
  const first = segments[0] ?? "";

  if (WORDPRESS_PREFIXES.some((prefix) => first === prefix || first.startsWith(prefix))) {
    return "/";
  }

  const direct = LEGACY_REDIRECTS[clean] ?? LEGACY_REDIRECTS[first];
  if (direct) return direct;

  // Oude plaatsadressen zoals /verhuizing-delft/ of /verhuisbedrijf-leiden/
  const city = first.replace(
    /^(verhuizing|verhuizingen|verhuizen|verhuisbedrijf|verhuizers|verhuizer|opslag|ontruiming|transport)-/,
    "",
  );
  if (city !== first && regions.some((r) => r.slug === city)) {
    return `/werkgebied/${city}`;
  }

  // Oude WordPress datum-permalinks zoals /2021/05/07/titel/
  if (/^\d{4}$/.test(first)) return "/verhuistips";

  return null;
}


export const Route = createFileRoute("/$")({
  beforeLoad: ({ params, location }) => {
    const raw = (params as { _splat?: string })._splat ?? location.pathname;
    const target = resolveLegacyTarget(raw);
    if (target) {
      throw redirect({ href: target, statusCode: 301, throw: true });
    }
    throw notFound();
  },
  component: () => null,
});
